import { useEffect } from 'react';
import { useTrackVisibility } from 'react-intersection-observer-hook';
import { useRecoilValue } from 'recoil';

import clientServices from '@audacy-clients/core/utils/clientServices';
import usePrevious from 'web/src/hooks/use-previous';
import { viewContext } from 'web/src/state/dataEvents';

type TTrackModuleViewProps = {
  moduleId: string | undefined;
  contentId: string | undefined;
};

/**
 * Hook to send a ModuleView Event for any component
 *
 * Note: This hook returns a `ref` that has to be tied to the component it wants to send the message for
 * @param moduleId - string
 * @param contentId - string | undefined
 */
export const useTrackModuleView = ({ moduleId, contentId }: TTrackModuleViewProps) => {
  const { viewId: viewContextViewId, contentId: viewContextContentId } =
    useRecoilValue(viewContext);
  const [moduleRef, { isVisible: isOnScreen }] = useTrackVisibility();
  const prevIsOnScreen = usePrevious(isOnScreen, false);

  useEffect(() => {
    // We only want to track the module view if the moduleId is defined
    if (!prevIsOnScreen && isOnScreen && moduleId) {
      clientServices.getAnalyticServices().sendModuleView({
        viewContext: {
          viewId: viewContextViewId,
          contentId: viewContextContentId,
        },
        moduleId: moduleId,
        contentId: contentId?.toLowerCase(),
      });
    }
  }, [isOnScreen, moduleId, contentId, prevIsOnScreen, viewContextContentId, viewContextViewId]);

  return moduleRef;
};
